import { Button, ErrorMessage, Fieldset, Layout, RadioButton } from '@vwfs-bronson/bronson-react';
import { FieldArray, useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PickPropertiesOfType } from '../../../services/common/miscUtils.types';
import { NumericInputFormField } from '../../Fieldset/NumericInputFormField';
import { ExpenseDetailsFormModel_PL, ExpenseGroupType } from '../privateLease/formModel';

type Props = {
  expenseFieldName: keyof PickPropertiesOfType<ExpenseDetailsFormModel_PL, ExpenseGroupType>;
  hideAddAndRemoveExpense?: boolean;
  maxNumberOfItemsToAdd?: number;
};

const defaultMaxNumberOfItemsToAdd = 4;

export const Expense: React.FC<Props> = ({ expenseFieldName, hideAddAndRemoveExpense, maxNumberOfItemsToAdd }) => {
  const { setFieldValue, values, errors, touched } = useFormikContext<ExpenseDetailsFormModel_PL>();
  const { t } = useTranslation();

  const addedMaxNumberOfItems =
    values[expenseFieldName]?.length >= (maxNumberOfItemsToAdd ?? defaultMaxNumberOfItemsToAdd);

  return (
    <Fieldset className="u-pt">
      <FieldArray name={expenseFieldName}>
        {({ push, remove }) => (
          <>
            {values[expenseFieldName].map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Layout key={`${index}_${values[expenseFieldName][index]}`}>
                {index === 0 ? (
                  <Layout.Item default="1/4" s="1/1">
                    <div className="u-text-left u-mt u-pt-small">
                      {t(`expenseDetailsForm:${expenseFieldName}:groupLabel`)}
                    </div>
                  </Layout.Item>
                ) : (
                  <Layout.Item default="1/4" s="1/1" />
                )}

                <Layout.Item default="3/4" s="1/1">
                  <Fieldset.Row>
                    <Layout>
                      <Layout.Item default="2/3" l="1/2" s="1/1">
                        <Layout>
                          <Layout.Item default="3/4" s="1/1">
                            <NumericInputFormField
                              fieldName={`${expenseFieldName}.${index}.payment`}
                              labelTranslationKey={`expenseDetailsForm:${expenseFieldName}:label`}
                              placeholderTranslationKey={`expenseDetailsForm:${expenseFieldName}:placeholder`}
                              type="money"
                            />
                          </Layout.Item>

                          <Layout.Item default="1/4" s="1/1">
                            {index === 0 && !hideAddAndRemoveExpense ? (
                              <Button
                                type="button"
                                className="u-mt"
                                secondary
                                onClick={() => push({ payment: [0], sole: null })}
                                ariaLabel="Add expense"
                                icon="semantic-zoom-in"
                                disabled={addedMaxNumberOfItems}
                              />
                            ) : (
                              !hideAddAndRemoveExpense && (
                                <Button
                                  type="button"
                                  className="u-mt"
                                  secondary
                                  onClick={() => remove(index)}
                                  disabled={values[expenseFieldName].length === 1}
                                  ariaLabel="Remove expense"
                                  icon="semantic-zoom-out"
                                />
                              )
                            )}
                          </Layout.Item>
                        </Layout>
                      </Layout.Item>

                      <Layout.Item default="1/3" l="1/2" s="1/1">
                        <div className="c-button-group--small u-mt-small u-pt-small">
                          <RadioButton
                            className="u-mr"
                            name={`${expenseFieldName}.${index}.sole`}
                            checked={values[expenseFieldName][index]['sole'] === true}
                            onChange={(e) => {
                              setFieldValue(`${expenseFieldName}.${index}.sole`, Boolean(e.target.value), true);
                            }}
                          >
                            Sole
                          </RadioButton>
                          <RadioButton
                            name={`${expenseFieldName}.${index}.sole`}
                            checked={values[expenseFieldName][index]['sole'] === false}
                            onChange={(e) => {
                              setFieldValue(`${expenseFieldName}.${index}.sole`, !e.target.value, true);
                            }}
                          >
                            Joint
                          </RadioButton>
                        </div>

                        <Fieldset.Row>
                          {touched[expenseFieldName]?.[index]?.sole &&
                            (errors[expenseFieldName]?.[index] as any)?.sole && (
                              <ErrorMessage> {t('validation:required')} </ErrorMessage>
                            )}
                        </Fieldset.Row>
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                </Layout.Item>
              </Layout>
            ))}
          </>
        )}
      </FieldArray>
    </Fieldset>
  );
};
